import React, { useState, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import "../../styles/AssignedExercisesList.css";
import { AiOutlineDelete, AiOutlineCheck, AiOutlineClose } from "react-icons/ai";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";
import { useToast } from "../commons/toast/ToastContext";
import AssignExerciseModal from "./AssignExerciseModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import RevokeConfirmationModal from "./RevokeConfirmationModal";

const AssignedExercisesList = ({ dogId, exercises }) => {
    const { t, i18n } = useTranslation(['exercises', 'common']);
    const toast = useToast();
    const [exerciseList, setExerciseList] = useState(Array.isArray(exercises) ? exercises : []);
    const [expandedId, setExpandedId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
    const [exerciseToDelete, setExerciseToDelete] = useState(null);
    const [exerciseToRevoke, setExerciseToRevoke] = useState(null);
    const [error, setError] = useState("");

    // ✅ Fetch assigned exercises for this dog
    const fetchAssignedExercises = useCallback(async () => {
        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/exercise/dog/${dogId}/assigned`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // API returns Page object, exercises are in 'content' property
            const data = response.data?.content || response.data;
            // Ensure we always have an array
            setExerciseList(Array.isArray(data) ? data : []);
            setError("");
        } catch {
            setError(t('exercises:fetchListError'));
        }
    }, [dogId]);

    // Get exercise name for modal display - with safety check
    const getExerciseName = (exerciseId) => {
        if (!Array.isArray(exerciseList)) return t('exercises:exercise');
        const exercise = exerciseList.find(e => e.id === exerciseId);
        return exercise?.exercise?.name || t('exercises:exercise');
    };

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    // ✅ Complete Exercise
    const completeExercise = async (exerciseId) => {
        try {
            const token = localStorage.getItem("jwt");
            await axios.put(`/api/v1/exercise/complete/${exerciseId}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setExerciseList((prevExercises) =>
                prevExercises.map((exercise) =>
                    exercise.id === exerciseId ? { ...exercise, completed: true, completionDate: new Date().toISOString() } : exercise
                )
            );
            toast.success(t('exercises:completeSuccess'), t('common:success'));
        } catch (error) {
            setError(t('exercises:completeError'));
            toast.error(t('exercises:completeError'), t('common:error'));
        }
    };

    // ✅ Revoke Completion
    const revokeCompletion = async () => {
        if (!exerciseToRevoke) return;
        try {
            const token = localStorage.getItem("jwt");
            await axios.put(`/api/v1/exercise/revoke-complete/${exerciseToRevoke}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setExerciseList((prevExercises) =>
                prevExercises.map((exercise) =>
                    exercise.id === exerciseToRevoke ? { ...exercise, completed: false, completionDate: null } : exercise
                )
            );
            setIsRevokeModalOpen(false);
            setExerciseToRevoke(null);
            toast.success(t('exercises:revokeSuccess'), t('common:success'));
        } catch (error) {
            setError(t('exercises:revokeError'));
            toast.error(t('exercises:revokeError'), t('common:error'));
        }
    };

    // ✅ Delete Exercise
    const deleteExercise = async () => {
        if (!exerciseToDelete) return;
        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(`/api/v1/exercise/assigned/${exerciseToDelete}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setExerciseList((prevExercises) => prevExercises.filter((exercise) => exercise.id !== exerciseToDelete));
            setIsDeleteModalOpen(false);
            toast.success(t('exercises:deleteSuccess'), t('common:success'));
        } catch (error) {
            setError(t('exercises:deleteError'));
            toast.error(t('exercises:deleteError'), t('common:error'));
        }
    };

    return (
        <div className="assigned-exercises-container">
            {/* ✅ "Assign Exercise" Button */}
            <div className="assigned-exercise-assign-container">
                <button className="assigned-exercise-assign-btn" onClick={() => setIsModalOpen(true)}>{t('exercises:assignExercise')}</button>
            </div>

            {/* ✅ Assign Exercise Modal */}
            <AssignExerciseModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={fetchAssignedExercises}
                dogId={dogId}
            />

            {/* ✅ Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={deleteExercise}
                itemName={getExerciseName(exerciseToDelete)}
            />

            {/* ✅ Revoke Confirmation Modal */}
            <RevokeConfirmationModal
                isOpen={isRevokeModalOpen}
                onClose={() => {
                    setIsRevokeModalOpen(false);
                    setExerciseToRevoke(null);
                }}
                onConfirm={revokeCompletion}
                itemName={getExerciseName(exerciseToRevoke)}
            />

            {error && <p className="assigned-exercise-error">{error}</p>}

            {/* ✅ Exercise List - New Card Design */}
            {exerciseList.length === 0 ? <p>{t('exercises:noAssignedExercises')}</p> : (
                <div className="assigned-exercise-list">
                    {/* ✅ Exercise Items - Card Style like Reports */}
                    {exerciseList.map((assignedExercise) => (
                        <div
                            key={assignedExercise.id}
                            className={`assigned-exercise-item ${assignedExercise.completed ? 'completed' : ''} ${expandedId === assignedExercise.id ? 'expanded' : ''}`}
                            onClick={() => toggleExpand(assignedExercise.id)}
                        >
                            <div className="assigned-exercise-header">
                                <div className="assigned-exercise-info">
                                    <h3 className="assigned-exercise-name">{assignedExercise.exercise?.name || t('exercises:noName')}</h3>
                                    <div className="assigned-exercise-meta">
                                        <span className={`assigned-exercise-difficulty assigned-exercise-difficulty-${assignedExercise.exercise?.difficultyLevel?.toLowerCase().replace(/\s+/g, '-') || 'basic'}`}>
                                            {t(`exercises:difficulty.${(assignedExercise.exercise?.difficultyLevel || 'basic').toLowerCase()}`, { defaultValue: assignedExercise.exercise?.difficultyLevel || t('exercises:unknown') })}
                                        </span>
                                        {assignedExercise.assignedDate && (
                                            <span className="assigned-exercise-date-badge">
                                                {t('exercises:assigned')}: {new Date(assignedExercise.assignedDate).toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US')}
                                            </span>
                                        )}
                                        {assignedExercise.completionDate && (
                                            <span className="assigned-exercise-completion-badge">
                                                {t('exercises:completed')}: {new Date(assignedExercise.completionDate).toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="assigned-exercise-actions">
                                    {assignedExercise.completed ? (
                                        <AiOutlineClose
                                            size={20}
                                            className="assigned-exercise-revoke-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setExerciseToRevoke(assignedExercise.id);
                                                setIsRevokeModalOpen(true);
                                            }}
                                            title={t('exercises:revokeCompletion')}
                                        />
                                    ) : (
                                        <AiOutlineCheck
                                            size={20}
                                            className="assigned-exercise-complete-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                completeExercise(assignedExercise.id);
                                            }}
                                            title={t('exercises:markAsCompleted')}
                                        />
                                    )}
                                    <AiOutlineDelete
                                        size={20}
                                        className="assigned-exercise-delete-icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExerciseToDelete(assignedExercise.id);
                                            setIsDeleteModalOpen(true);
                                        }}
                                        title={t('exercises:deleteExercise')}
                                    />
                                </div>
                            </div>

                            {/* ✅ Expandable Details */}
                            {expandedId === assignedExercise.id && (
                                <div className="assigned-exercise-details">
                                    <div className="assigned-exercise-details-content">
                                        <div className="assigned-exercise-detail-section">
                                            <p className="assigned-exercise-detail-title">{t('exercises:description')}</p>
                                            <p className="assigned-exercise-detail-text">{assignedExercise.exercise?.description || t('exercises:noDescription')}</p>
                                        </div>
                                        {assignedExercise.exercise?.requiredEquipment && (
                                            <div className="assigned-exercise-detail-section">
                                                <p className="assigned-exercise-detail-title">{t('exercises:requiredEquipment')}</p>
                                                <p className="assigned-exercise-detail-text">{assignedExercise.exercise.requiredEquipment}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AssignedExercisesList;